import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';

export interface QueensCell {
    row: number;
    col: number;
    regionId: number;
    hasQueen: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class QueensService {

    constructor(private langService: LanguageService) { }

    generatePuzzle(size: number, difficulty: 'easy' | 'medium' | 'hard' = 'medium'): { cells: QueensCell[][], solution: { row: number, col: number }[], prefilledMarks: { row: number, col: number }[] } {
        let attempts = 0;
        const MAX_ATTEMPTS = 100;

        while (attempts < MAX_ATTEMPTS) {
            attempts++;

            // 1. Find a valid queen placement first (the solution)
            const solution = this.findValidSolution(size);

            // 2. Generate regions around those queens
            // Try multiple region configurations for the same solution to save time
            for (let rTry = 0; rTry < 10; rTry++) {
                const regions = this.generateRegions(size, solution);

                // 3. Check for Uniqueness
                // If the generated regions allow ONLY the intended solution, we are good.
                if (this.hasUniqueSolution(size, regions)) {
                    // 4. Create the final cells
                    const cells: QueensCell[][] = [];
                    for (let r = 0; r < size; r++) {
                        cells[r] = [];
                        for (let c = 0; c < size; c++) {
                            cells[r][c] = {
                                row: r,
                                col: c,
                                regionId: regions[r][c],
                                hasQueen: false
                            };
                        }
                    }
                    return { cells, solution, prefilledMarks: [] };
                }
            }
        }

        // Fallback (extremely unlikely to fail 100x10 attempts)
        // Just return the last generated one even if not unique to avoid crash
        console.warn("Could not generate unique Queens puzzle in time");
        return this.generatePuzzle(size); // recursive retry? or simplified fallback
    }

    private hasUniqueSolution(size: number, regions: number[][]): boolean {
        let solutionsFound = 0;

        // Helper for backtracking
        const solve = (row: number, cols: number[], placedQueens: { r: number, c: number }[]) => {
            if (solutionsFound > 1) return; // Stop if non-unique

            if (row === size) {
                solutionsFound++;
                return;
            }

            for (let col = 0; col < size; col++) {
                // Optimization: Check basic row/col constraints immediately
                if (cols.includes(col)) continue;

                // Check Region and Proximity constraints
                let safe = true;
                const regionId = regions[row][col];

                for (const q of placedQueens) {
                    // Region Constraint
                    if (regions[q.r][q.c] === regionId) {
                        safe = false; break;
                    }
                    // Proximity Constraint (Touch)
                    // Row is guaranteed > q.r because we go row by row
                    // So just check if diffRow <= 1 (which is always 1 if row is q.r+1, but we recursion)
                    // Actually check logic: abs(r-q.r)<=1 && abs(c-q.c)<=1
                    if (Math.abs(row - q.r) <= 1 && Math.abs(col - q.c) <= 1) {
                        safe = false; break;
                    }
                }

                if (safe) {
                    cols.push(col);
                    placedQueens.push({ r: row, c: col });
                    solve(row + 1, cols, placedQueens);
                    placedQueens.pop();
                    cols.pop();
                }
            }
        };

        solve(0, [], []);
        return solutionsFound === 1;
    }

    private findValidSolution(size: number): { row: number, col: number }[] {
        const rows = Array.from({ length: size }, (_, i) => i);
        const cols = Array.from({ length: size }, (_, i) => i);

        while (true) {
            const shuffledCols = [...cols].sort(() => Math.random() - 0.5);
            const candidate: { row: number, col: number }[] = [];
            let possible = true;

            for (let r = 0; r < size; r++) {
                const c = shuffledCols[r];
                for (const q of candidate) {
                    if (Math.abs(q.row - r) <= 1 && Math.abs(q.col - c) <= 1) {
                        possible = false;
                        break;
                    }
                }
                if (!possible) break;
                candidate.push({ row: r, col: c });
            }

            if (candidate.length === size) return candidate;
        }
    }

    private generateRegions(size: number, solution: { row: number, col: number }[]): number[][] {
        const regions = Array.from({ length: size }, () => Array(size).fill(-1));

        solution.forEach((q, idx) => {
            regions[q.row][q.col] = idx;
        });

        const directions = [[0, 1], [0, -1], [1, 0], [-1, 0]];
        let unassigned = size * size - size;

        // Optimized region growing:
        // Maintains adjacency list to ensure we don't scan full grid every time
        // or just simple loop is fine for small grids (8x8)
        while (unassigned > 0) {
            let madeProgress = false;
            for (let r = 0; r < size; r++) {
                for (let c = 0; c < size; c++) {
                    if (regions[r][c] !== -1) {
                        // Try to grow
                        // Random shuffle directions to vary shapes
                        const dirs = [...directions].sort(() => Math.random() - 0.5);
                        for (const [dr, dc] of dirs) {
                            const nr = r + dr;
                            const nc = c + dc;
                            if (nr >= 0 && nr < size && nc >= 0 && nc < size && regions[nr][nc] === -1) {
                                // Random chance to skip growing to allow other regions to catch up? 
                                // Or just grow.
                                regions[nr][nc] = regions[r][c];
                                unassigned--;
                                madeProgress = true;
                                // Break to distribute growth? No, flood fill style is ok.
                                // But strictly growing one neighbor per pass creates better shapes?
                                // Let's break after one growth to make it balanced
                                break;
                            }
                        }
                    }
                }
            }
            // Failsafe if stuck (shouldn't happen with flood fill but capable of islands)
            // If we have unassigned nodes but no one could grow to them (islands), we must assign them to nearest?
            // Existing logic was naive but worked. Let's keep it robust.
            if (!madeProgress && unassigned > 0) {
                // Find unassigned and force assign to neighbor
                for (let r = 0; r < size; r++) {
                    for (let c = 0; c < size; c++) {
                        if (regions[r][c] === -1) {
                            // grab any neighbor
                            for (const [dr, dc] of directions) {
                                const nr = r + dr, nc = c + dc;
                                if (nr >= 0 && nr < size && nc >= 0 && nc < size && regions[nr][nc] !== -1) {
                                    regions[r][c] = regions[nr][nc];
                                    unassigned--;
                                    madeProgress = true;
                                    break;
                                }
                            }
                        }
                    }
                }
            }
        }

        return regions;
    }

    private getRegionName(id: number): string {
        const colors = ["Rouge", "Violette", "Verte", "Jaune", "Bleue", "Rose", "Cyan", "Lime", "Marron", "Gris"];
        return colors[id % colors.length] || `Zone ${id}`;
    }

    validatePlacement(cells: QueensCell[][], size: number): { valid: boolean, errors: { row: number, col: number }[] } {
        const queens: { row: number, col: number, regionId: number }[] = [];
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (cells[r][c].hasQueen) queens.push({ row: r, col: c, regionId: cells[r][c].regionId });
            }
        }

        const errors: Set<string> = new Set();

        for (let i = 0; i < queens.length; i++) {
            for (let j = i + 1; j < queens.length; j++) {
                const q1 = queens[i];
                const q2 = queens[j];

                if (q1.row === q2.row) {
                    errors.add(`${q1.row},${q1.col}`);
                    errors.add(`${q2.row},${q2.col}`);
                }
                if (q1.col === q2.col) {
                    errors.add(`${q1.row},${q1.col}`);
                    errors.add(`${q2.row},${q2.col}`);
                }
                if (q1.regionId === q2.regionId) {
                    errors.add(`${q1.row},${q1.col}`);
                    errors.add(`${q2.row},${q2.col}`);
                }
                if (Math.abs(q1.row - q2.row) <= 1 && Math.abs(q1.col - q2.col) <= 1) {
                    errors.add(`${q1.row},${q1.col}`);
                    errors.add(`${q2.row},${q2.col}`);
                }
            }
        }

        return {
            valid: errors.size === 0 && queens.length === size,
            errors: Array.from(errors).map(s => {
                const [r, c] = s.split(',').map(Number);
                return { row: r, col: c };
            })
        };
    }

    getHint(cells: QueensCell[][], solution: { row: number, col: number }[], marks: boolean[][], autoMarks: boolean[][]): { row: number, col: number, type: 'queen' | 'mark', explanation: string } | null {
        const size = cells.length;
        const isMarked = (r: number, c: number) => marks[r][c] || autoMarks[r][c];
        const t = (key: string, params: any = {}) => this.langService.translate(key, params);

        // 1, 2, 3, 4. Élimination par Reine (Ligne, Colonne, Zone, Proximité)
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (cells[r][c].hasQueen) {
                    // Proximité (Direct et Diagonal)
                    for (let dr = -1; dr <= 1; dr++) {
                        for (let dc = -1; dc <= 1; dc++) {
                            const nr = r + dr, nc = c + dc;
                            if (nr >= 0 && nr < size && nc >= 0 && nc < size && (dr !== 0 || dc !== 0)) {
                                if (!cells[nr][nc].hasQueen && !isMarked(nr, nc)) {
                                    return { row: nr, col: nc, type: 'mark', explanation: t('queens_hint_proximity') };
                                }
                            }
                        }
                    }
                    // Ligne
                    for (let i = 0; i < size; i++) {
                        if (i !== c && !cells[r][i].hasQueen && !isMarked(r, i)) {
                            return { row: r, col: i, type: 'mark', explanation: t('queens_hint_row') };
                        }
                    }
                    // Colonne
                    for (let i = 0; i < size; i++) {
                        if (i !== r && !cells[i][c].hasQueen && !isMarked(i, c)) {
                            return { row: i, col: c, type: 'mark', explanation: t('queens_hint_col') };
                        }
                    }
                    // Zone
                    const regionId = cells[r][c].regionId;
                    for (let i = 0; i < size; i++) {
                        for (let j = 0; j < size; j++) {
                            if (cells[i][j].regionId === regionId && (i !== r || j !== c) && !cells[i][j].hasQueen && !isMarked(i, j)) {
                                return { row: i, col: j, type: 'mark', explanation: t('queens_hint_zone') };
                            }
                        }
                    }
                }
            }
        }

        // 5, 6. Confinement de Régions (Ligne/Colonne)
        const regionPossibilities = new Map<number, { r: number, c: number }[]>();
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (!isMarked(r, c) && !cells[r][c].hasQueen) {
                    const id = cells[r][c].regionId;
                    if (!regionPossibilities.has(id)) regionPossibilities.set(id, []);
                    regionPossibilities.get(id)!.push({ r, c });
                }
            }
        }

        for (const [id, points] of regionPossibilities) {
            const rows = new Set(points.map(p => p.r));
            const cols = new Set(points.map(p => p.c));

            if (rows.size === 1) {
                const r = Array.from(rows)[0];
                for (let c = 0; c < size; c++) {
                    if (cells[r][c].regionId !== id && !isMarked(r, c) && !cells[r][c].hasQueen) {
                        return { row: r, col: c, type: 'mark', explanation: t('queens_hint_confinement_row') };
                    }
                }
            }
            if (cols.size === 1) {
                const c = Array.from(cols)[0];
                for (let r = 0; r < size; r++) {
                    if (cells[r][c].regionId !== id && !isMarked(r, c) && !cells[r][c].hasQueen) {
                        return { row: r, col: c, type: 'mark', explanation: t('queens_hint_confinement_col') };
                    }
                }
            }
        }

        // 7. Règle du Groupe de Zones (Naked Subsets of Regions)
        // Check for N regions confined to the same N rows
        const activeRegions = Array.from(regionPossibilities.entries())
            .filter(([id]) => !cells.some(row => row.some(cell => cell.regionId === id && cell.hasQueen)));

        if (activeRegions.length > 2) {
            // Check for pairs (N=2) - simplified for performance, can be expanded to N=3
            for (let i = 0; i < activeRegions.length; i++) {
                for (let j = i + 1; j < activeRegions.length; j++) {
                    const [id1, points1] = activeRegions[i];
                    const [id2, points2] = activeRegions[j];

                    const rows1 = new Set(points1.map(p => p.r));
                    const rows2 = new Set(points2.map(p => p.r));
                    const unionRows = new Set([...rows1, ...rows2]);

                    if (unionRows.size === 2) {
                        // Found 2 regions confined to 2 rows. Eliminate others in these rows.
                        for (const r of Array.from(unionRows)) {
                            for (let c = 0; c < size; c++) {
                                if (cells[r][c].regionId !== id1 && cells[r][c].regionId !== id2 && !isMarked(r, c) && !cells[r][c].hasQueen) {
                                    return { row: r, col: c, type: 'mark', explanation: t('queens_hint_group', { z1: this.getRegionName(id1), z2: this.getRegionName(id2), type: t('row') + 's' }) };
                                }
                            }
                        }
                    }

                    const cols1 = new Set(points1.map(p => p.c));
                    const cols2 = new Set(points2.map(p => p.c));
                    const unionCols = new Set([...cols1, ...cols2]);

                    if (unionCols.size === 2) {
                        // Found 2 regions confined to 2 columns.
                        for (const c of Array.from(unionCols)) {
                            for (let r = 0; r < size; r++) {
                                if (cells[r][c].regionId !== id1 && cells[r][c].regionId !== id2 && !isMarked(r, c) && !cells[r][c].hasQueen) {
                                    return { row: r, col: c, type: 'mark', explanation: t('queens_hint_group', { z1: this.getRegionName(id1), z2: this.getRegionName(id2), type: t('col') + 's' }) };
                                }
                            }
                        }
                    }
                }
            }
        }

        // 8. Règle de l'Impact Indirect (Look-ahead)
        // Check if placing a queen in an empty cell would block another region entirely.
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (!isMarked(r, c) && !cells[r][c].hasQueen) {
                    const currentRegionId = cells[r][c].regionId;

                    // Simulate placing a queen at (r, c)
                    for (const [otherRegionId, points] of regionPossibilities) {
                        if (otherRegionId === currentRegionId) continue;

                        // Check if any point in otherRegionId would still be valid if (r, c) has a queen
                        const wouldHavePlacement = points.some(p => {
                            // Point p is valid if it's NOT in the same row, col, or adjacent to (r, c)
                            return p.r !== r && p.c !== c && (Math.abs(p.r - r) > 1 || Math.abs(p.c - c) > 1);
                        });

                        if (!wouldHavePlacement) {
                            return {
                                row: r,
                                col: c,
                                type: 'mark',
                                explanation: t('queens_hint_indirect', { target: this.getRegionName(otherRegionId) })
                            };
                        }
                    }
                }
            }
        }

        // 9. Reine Forcée (Dernière case libre)
        // Vérification par Zone
        for (const [id, points] of regionPossibilities) {
            const hasQueenInRegion = cells.some(row => row.some(cell => cell.regionId === id && cell.hasQueen));
            if (!hasQueenInRegion && points.length === 1) {
                return { row: points[0].r, col: points[0].c, type: 'queen', explanation: t('queens_hint_forced', { type: t('zone') }) };
            }
        }
        // Vérification par Ligne
        for (let r = 0; r < size; r++) {
            const pointsInRow = [];
            let hasQueen = false;
            for (let c = 0; c < size; c++) {
                if (cells[r][c].hasQueen) hasQueen = true;
                if (!isMarked(r, c) && !cells[r][c].hasQueen) pointsInRow.push({ r, c });
            }
            if (!hasQueen && pointsInRow.length === 1) {
                return { row: pointsInRow[0].r, col: pointsInRow[0].c, type: 'queen', explanation: t('queens_hint_forced', { type: t('row') }) };
            }
        }
        // Vérification par Colonne
        for (let c = 0; c < size; c++) {
            const pointsInCol = [];
            let hasQueen = false;
            for (let r = 0; r < size; r++) {
                if (cells[r][c].hasQueen) hasQueen = true;
                if (!isMarked(r, c) && !cells[r][c].hasQueen) pointsInCol.push({ r, c });
            }
            if (!hasQueen && pointsInCol.length === 1) {
                return { row: pointsInCol[0].r, col: pointsInCol[0].c, type: 'queen', explanation: t('queens_hint_forced', { type: t('col') }) };
            }
        }

        // Fallback Solution (Garantie de progression)
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                const isSolutionQueen = solution.some(s => s.row === r && s.col === c);
                if (isSolutionQueen && !cells[r][c].hasQueen) {
                    return { row: r, col: c, type: 'queen', explanation: t('queens_hint_solution') };
                }
                if (!isSolutionQueen && !isMarked(r, c) && !cells[r][c].hasQueen) {
                    return { row: r, col: c, type: 'mark', explanation: t('queens_hint_solution') };
                }
            }
        }

        return null;
    }
}
